/**
 * OrganizationsV3
 * Flag: OrganizationsV3
 * API: POST api/v3/organizations/search  (paginated)
 *
 * Full rewrite of Search.tsx (v2) for HCO organizations:
 *   - react-hook-form for search/filter state (no class component)
 *   - RTK Query useMutation (no raw apiFetch)
 *   - AG Grid Community for results (no custom table)
 *   - Server-side pagination via pageNumber/pageSize
 *   - Ant Design Drawer for preview
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Drawer } from 'antd';

import styles from './OrganizationsV3.module.css';
import { useSearchOrganizationsV3Mutation } from '../../api/organizationsV3Api';
import type { HcoSearchRowV3 } from '../../api/organizationsV3Api';
import SearchLanding from '../../globalcomponents/SearchLanding';
import apiFetch from '../../api/apiFetch';
// Reused as-is from the v2 search page — self-contained (own wizard pages +
// Popup), only needs user/submit/close. No V3-native rewrite exists yet.
// It's an untyped (@ts-nocheck) legacy class component, so its props are
// cast explicitly for this typed call site.
import AddHcoUntyped from '../Search/components/AddHco';
const AddHco = AddHcoUntyped as unknown as React.ComponentType<{
    user: Props['user'];
    submit: (json: Record<string, unknown>) => void;
    close: () => void;
}>;

import NotificationBoxUntyped from '../../globalcomponents/NotificationBox';
import YesNoDialogBoxUntyped from '../../globalcomponents/YesNoDialogBox';
const NotificationBox = NotificationBoxUntyped as unknown as React.ComponentType<{
    message: string;
    errorStatus: boolean;
    callback: () => void;
}>;
const YesNoDialogBox = YesNoDialogBoxUntyped as unknown as React.ComponentType<{
    title: string;
    msgFirstLine: string;
    msgSecondLine: string;
    leftButton: string;
    rightButton: string;
    leftClick: () => void;
    rightClick: () => void;
}>;

import SearchPanel, { type SearchFormValues } from './components/SearchPanel';
import FilterPanel from './components/FilterPanel';
import HcoGrid from './components/HcoGrid';
import PaginationBar from './components/PaginationBar';
import {
    isDermRequestor, rowKey, toggleState,
    type FlagKind, type FlagOverrides, type ToggleState,
} from './flagRules';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Props {
    user?: {
        team?:     string;
        terr?:     string;
        email?:    string;
        userName?: string;
        role?:     string;
        isDermAdmin?: string;
    };
}

interface Notice {
    message: string;
    isError: boolean;
}

// Default form values
const DEFAULT_VALUES: SearchFormValues = {
    orgName:  '',
    city:     '',
    state:    '',
    zip:      '',
    orgId:    '',
    npi:      '',
    tin:      '',
    address:  '',
    universe: '',
    orgType:  '',
    showAll:  'N',
};

// ---------------------------------------------------------------------------
// Preview panel content
// ---------------------------------------------------------------------------

function PreviewContent({ row }: { row: HcoSearchRowV3 }) {
    const address = [row.address1, row.address2, row.city, row.state, row.zip5]
        .filter(Boolean).join(', ');

    return (
        <div style={{ fontSize: 14, lineHeight: 1.7 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
                {row.orgName ?? '—'}
            </div>
            {row.orgType    && <div><strong>Type:</strong> {row.orgType}</div>}
            {address        && <div><strong>Address:</strong> {address}</div>}
            {row.phone      && <div><strong>Phone:</strong> {row.phone}</div>}
            {row.fax        && <div><strong>Fax:</strong> {row.fax}</div>}
            {row.email      && <div><strong>Email:</strong> {row.email}</div>}
            {row.npi        && <div><strong>NPI:</strong> {row.npi}</div>}
            {row.tin        && <div><strong>TIN:</strong> {row.tin}</div>}
            {row.terrId     && <div><strong>Territory:</strong> {row.terrId}</div>}
            {row.affiliationCount !== null && row.affiliationCount !== undefined && (
                <div><strong>Affiliations:</strong> {row.affiliationCount}</div>
            )}
            <div style={{ marginTop: 12 }}>
                <strong>CTMS:</strong> {row.ctmsFlag === 'Y' ? '✔' : '—'}
                {'  '}
                <strong>Spend:</strong> {row.aggspendFlag === 'Y' ? '✔' : '—'}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function OrganizationsV3({ user }: Props) {
    const { register, handleSubmit, watch, setValue, reset, getValues } =
        useForm<SearchFormValues>({ defaultValues: DEFAULT_VALUES });

    // Pagination state
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize,   setPageSize]   = useState(20);

    // Store last committed search params so page changes can re-run
    // without requiring the user to re-submit the form.
    const lastSearchRef = useRef<SearchFormValues>(DEFAULT_VALUES);

    // RTK Query
    const [searchOrganizationsV3, { data, isLoading, isError }] = useSearchOrganizationsV3Mutation();

    // Preview drawer
    const [previewRow,  setPreviewRow]  = useState<HcoSearchRowV3 | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    // Add Organization wizard
    const [showAddOrg, setShowAddOrg] = useState(false);

    // -----------------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------------

    // Mirrors SearchResults.tsx's submit() (v2) — same requestor stamping and
    // Derm-team override, same endpoint.
    const submitAddOrganization = useCallback((json: Record<string, unknown>) => {
        json.REQUESTOR_ID = user?.email;
        json.REQUESTOR_NAME = user?.userName;
        json.REQUESTOR_TEAM = user?.team;
        json.REQUESTOR_TERR = user?.terr;
        if (isDermRequestor(user)) {
            json.REQUESTOR_TEAM = '31';
        }
        void apiFetch('/api/v2/global/change-request', json);
    }, [user]);

    // -----------------------------------------------------------------------
    // CRM / CTMS / Spend toggles — same requests and messages as v2 Search.tsx addTo()
    // -----------------------------------------------------------------------

    const [flagOverrides, setFlagOverrides] = useState<FlagOverrides>({});
    const [flagErrorKey,  setFlagErrorKey]  = useState<string | null>(null);
    const [notice,        setNotice]        = useState<Notice | null>(null);
    const [pendingCrmRemoval, setPendingCrmRemoval] = useState<HcoSearchRowV3 | null>(null);

    // Local toggle changes only apply to the results they were made on.
    useEffect(() => {
        setFlagOverrides({});
        setFlagErrorKey(null);
        setNotice(null);
    }, [data]);

    const setOverride = useCallback((row: HcoSearchRowV3, changes: Partial<Record<FlagKind, boolean>>) => {
        const key = rowKey(row);
        setFlagOverrides(prev => ({ ...prev, [key]: { ...prev[key], ...changes } }));
    }, []);

    const submitFlagRequest = useCallback((row: HcoSearchRowV3, requestType: string) =>
        apiFetch('/api/v2/global/change-request', {
            REQUEST_TYPE:   requestType,
            ORG_ID:         row.orgId,
            REQUESTOR_ID:   user?.email,
            REQUESTOR_NAME: user?.userName,
            REQUESTOR_TEAM: isDermRequestor(user) ? '31' : user?.team,
            REQUESTOR_TERR: user?.terr,
        }), [user]);

    const applyToggle = useCallback(async (row: HcoSearchRowV3, kind: FlagKind, wasOn: boolean) => {
        const spendWasOn = toggleState('Spend', row, user, flagOverrides).active;
        const alsoAddSpend = kind === 'CRM' && !wasOn && !spendWasOn;

        setOverride(row, alsoAddSpend ? { CRM: true, Spend: true } : { [kind]: !wasOn });
        setFlagErrorKey(null);
        setNotice({
            isError: false,
            message: alsoAddSpend
                ? 'You switched CRM and Spend to ON for this organization.'
                : `You switched ${kind} to ${wasOn ? 'OFF' : 'ON'} for this organization.`,
        });

        try {
            await submitFlagRequest(row, `${wasOn ? 'Remove from' : 'Add to'} - ${kind}`);
            if (alsoAddSpend) await submitFlagRequest(row, 'Add to - Spend');
        } catch {
            setOverride(row, alsoAddSpend ? { CRM: wasOn, Spend: spendWasOn } : { [kind]: wasOn });
            setNotice({ isError: true, message: `Your ${kind} change could not be submitted. Please try again.` });
        }
    }, [user, flagOverrides, setOverride, submitFlagRequest]);

    const handleToggle = useCallback((row: HcoSearchRowV3, kind: FlagKind, state: ToggleState) => {
        if (state.disabled) {
            setFlagErrorKey(`${rowKey(row)}|${kind}`);
            setNotice({
                isError: true,
                message: `You cannot ${state.active ? 'remove' : 'add'} this organization from your ${kind}.`,
            });
            return;
        }
        if (kind === 'CRM' && state.active && (row.affiliationCount ?? 0) > 0) {
            setPendingCrmRemoval(row);
            return;
        }
        void applyToggle(row, kind, state.active);
    }, [applyToggle]);

    const runSearch = useCallback(
        (values: SearchFormValues, page: number, size: number) => {
            lastSearchRef.current = values;
            searchOrganizationsV3({
                search: {
                    orgName:  values.orgName  || undefined,
                    city:     values.city     || undefined,
                    state:    values.state    || undefined,
                    zip:      values.zip      || undefined,
                    orgId:    values.orgId    || undefined,
                    npi:      values.npi      || undefined,
                    tin:      values.tin      || undefined,
                    address:  values.address  || undefined,
                    universe: values.universe || undefined,
                    orgType:  values.orgType  || undefined,
                    showAll:  values.showAll  || undefined,
                },
                user: {
                    team:      user?.team,
                    territory: user?.terr,
                },
                pageNumber: page,
                pageSize:   size,
            });
        },
        [searchOrganizationsV3, user]
    );

    const handleSearch = useCallback(() => {
        const values = getValues();
        setPageNumber(1);
        runSearch(values, 1, pageSize);
    }, [getValues, pageSize, runSearch]);

    const handleApplyFilter = useCallback(() => {
        const values = getValues();
        setPageNumber(1);
        runSearch(values, 1, pageSize);
    }, [getValues, pageSize, runSearch]);

    const handleClearAll = useCallback(() => {
        reset(DEFAULT_VALUES);
        setPageNumber(1);
    }, [reset]);

    const handleClearFilter = useCallback(() => {
        setValue('universe', '');
        setValue('orgType',  '');
        setValue('showAll',  'N');
        const values = { ...getValues(), universe: '', orgType: '', showAll: 'N' };
        setPageNumber(1);
        runSearch(values, 1, pageSize);
    }, [setValue, getValues, pageSize, runSearch]);

    const handlePageChange = useCallback((page: number) => {
        setPageNumber(page);
        runSearch(lastSearchRef.current, page, pageSize);
    }, [pageSize, runSearch]);

    const handlePageSizeChange = useCallback((size: number) => {
        setPageSize(size);
        setPageNumber(1);
        runSearch(lastSearchRef.current, 1, size);
    }, [runSearch]);

    const handlePreview = useCallback((row: HcoSearchRowV3) => {
        setPreviewRow(row);
        setPreviewOpen(true);
    }, []);

    const handleViewDetails = useCallback((row: HcoSearchRowV3) => {
        // Navigate to v2 HCO details page — v3 details rewrite is a future phase
        if (row.orgId) {
            window.location.href = `/hco/details/${row.orgId}`;
        }
    }, []);

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    const rows       = data?.rows       ?? [];
    const totalCount = data?.totalCount ?? 0;
    const hasResults = data !== undefined;

    return (
        <div className={styles.page}>
            {/* Search panel */}
            <SearchPanel
                register={register}
                setValue={setValue}
                onSearch={handleSearch}
                onClearAll={handleClearAll}
                isLoading={isLoading}
            />

            {/* Filter panel */}
            <FilterPanel
                register={register}
                onApply={handleApplyFilter}
                onClearFilter={handleClearFilter}
                isLoading={isLoading}
            />

            {/* Results area */}
            <div className={styles.resultsArea}>
                {isLoading && (
                    <div className={styles.landing} data-testid="loading-state">
                        Loading…
                    </div>
                )}

                {!isLoading && isError && (
                    <div className={styles.errorMsg} data-testid="error-state">
                        Error loading Organizations. Please try again.
                    </div>
                )}

                {!isLoading && !isError && !hasResults && (
                    <div data-testid="landing-state">
                        <SearchLanding type="/hco" />
                    </div>
                )}

                {!isLoading && !isError && hasResults && (
                    <>
                        {notice && (
                            <div data-testid="flag-notice">
                                <NotificationBox
                                    message={notice.message}
                                    errorStatus={notice.isError}
                                    callback={() => { setNotice(null); setFlagErrorKey(null); }}
                                />
                            </div>
                        )}
                        <PaginationBar
                            totalCount={totalCount}
                            pageNumber={pageNumber}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                        <HcoGrid
                            rows={rows}
                            onPreview={handlePreview}
                            onViewDetails={handleViewDetails}
                            user={user}
                            overrides={flagOverrides}
                            errorKey={flagErrorKey}
                            onToggle={handleToggle}
                        />
                        <PaginationBar
                            totalCount={totalCount}
                            pageNumber={pageNumber}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                        <div className={styles.bottomBar}>
                            <button
                                type="button"
                                className={styles.addButton}
                                data-testid="btn-addOrganization"
                                onClick={() => setShowAddOrg(true)}
                            >
                                + ADD ORGANIZATION
                            </button>
                        </div>
                    </>
                )}
            </div>

            {pendingCrmRemoval && (
                <YesNoDialogBox
                    title="Remove From CRM"
                    msgFirstLine="The selected organization has one or more affiliations."
                    msgSecondLine="Select No to go back.  Select Yes to remove from CRM."
                    leftButton="NO"
                    rightButton="YES"
                    leftClick={() => setPendingCrmRemoval(null)}
                    rightClick={() => {
                        const row = pendingCrmRemoval;
                        setPendingCrmRemoval(null);
                        void applyToggle(row, 'CRM', true);
                    }}
                />
            )}

            {showAddOrg && (
                <AddHco
                    user={user}
                    submit={submitAddOrganization}
                    close={() => setShowAddOrg(false)}
                />
            )}

            {/* Preview drawer (Ant Design) */}
            <Drawer
                title={previewRow?.orgName ?? 'Preview'}
                placement="right"
                size={420}
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                data-testid="preview-drawer"
            >
                {previewRow && <PreviewContent row={previewRow} />}
            </Drawer>
        </div>
    );
}

